/**
 * The voices dialog: what the narrator channel has said, on request, in a
 * modal — the journal dialog's pattern, voice-side, for the same reasons
 * (native `<dialog>` + `showModal()` buys focus trapping, `Escape`, the top
 * layer, and focus restoration; jsdom lacks `showModal`, so the shared setup
 * shims it and the real behavior is Playwright + axe territory).
 *
 * The component owns only "is it open" — the index itself stays
 * {@link VoicesPanelComponent}, pure and prop-driven, so exactly one thing
 * knows how to render an utterance.
 *
 * @packageDocumentation
 */

import { type SpeakerView, type VoiceIndexView } from '@orpheus/api-contracts';
import { useState } from 'react';

import { ModalCornerCloseComponent } from '../modal-dialog/modal-corner-close-component.js';
import { showModalWhileMounted } from '../modal-dialog/show-modal-while-mounted.js';
import { spokenLineCountOf, VoicesPanelComponent } from '../voices-panel/voices-panel-component.js';

/** Props for {@link VoicesDialogComponent}. */
export interface VoicesDialogComponentProps {
	/** D192: the pack's declared speakers — passed through to the panel for each index's color. */
	readonly speakers: readonly SpeakerView[];
	/** The engine's voice-index derivation, one per speaker — passed through to the panel. */
	readonly voices: readonly VoiceIndexView[];
	/**
	 * Called once the dialog has closed, by any route — the Close button, the corner ✕ or
	 * `Escape`. The native dialog returns focus to the trigger; the shell uses
	 * this to send it on to the directive line instead, so a player who
	 * consulted the index can type the next move without a click (Mike,
	 * 2026-09-10). Optional: without it, the native behavior stands.
	 */
	readonly onDismissed?: () => void;
}

/**
 * Render the voices trigger and its modal dialog.
 *
 * @param props - {@link VoicesDialogComponentProps}
 * @returns The trigger button and the dialog it opens
 */
export const VoicesDialogComponent = ({ onDismissed, speakers, voices }: VoicesDialogComponentProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const spokenCount = spokenLineCountOf(voices);

	return (
		<>
			<button
				aria-haspopup="dialog"
				// Named in full for assistive tech; the visible chrome stays compact.
				aria-label={`Voices — ${String(spokenCount)} line${spokenCount === 1 ? '' : 's'} spoken`}
				className="voices-dialog__trigger"
				onClick={() => {
					setIsOpen(true);
				}}
				type="button"
			>
				<VoicesIcon />
				<span className="voices-dialog__trigger-label">Voices</span>
				<span aria-hidden="true" className="voices-dialog__trigger-count">
					{spokenCount}
				</span>
			</button>
			{isOpen ? (
				<dialog
					aria-labelledby="voices-panel-heading"
					className="voices-dialog"
					// The ONE route out. Escape closes the element natively; the Close
					// button closes it too (below) rather than dropping the flag first —
					// while the modal is up the page behind it is inert, so focus sent
					// to the directive line then would be refused. Only once the
					// element has closed (and the browser has put focus back on the
					// trigger) can the shell send it on, which is what `onDismissed`
					// is for.
					onClose={() => {
						setIsOpen(false);
						onDismissed?.();
					}}
					// The element exists exactly while the index is open — the map
					// dialog's construction, through the one shared ref (D172).
					ref={showModalWhileMounted}
				>
					<div className="voices-dialog__body">
						<VoicesPanelComponent speakers={speakers} voices={voices} />
						<button
							className="voices-dialog__close"
							onClick={(event) => {
								event.currentTarget.closest('dialog')?.close();
							}}
							type="button"
						>
							Close
						</button>
					</div>
					<ModalCornerCloseComponent label="Close voices" />
				</dialog>
			) : null}
		</>
	);
};

/**
 * The trigger's speech-line glyph. Decorative — the button is named by its
 * visible label, so the icon is hidden from assistive technology.
 *
 * @returns The icon
 */
const VoicesIcon = () => (
	<svg
		aria-hidden="true"
		className="voices-dialog__icon"
		fill="none"
		focusable="false"
		height="16"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		strokeWidth={1.6}
		viewBox="0 0 24 24"
		width="16"
	>
		<path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12Z" />
		<path d="M8.5 11h.01" />
		<path d="M12 11h.01" />
		<path d="M15.5 11h.01" />
	</svg>
);
